import { Box, Grid, Portal, Stack, Typography, styled } from "@mui/material";
import {
  AttachMoney,
  HubOutlined,
  Inventory2Outlined,
  CategoryOutlined,
  LocalOfferOutlined,
} from "@mui/icons-material";
import {
  AnalyticsChartStatCard,
  ImpressionsByDayChart,
  ProductBrandsDropdown,
  table5Rows,
} from "features/analytics";
import {
  ProductCategoriesDropdown,
  PrimaryTopMicroConversions,
  ProductsDropdown,
} from "features/analytics";
import { useSelectedProject } from "Hooks";
import { useProducts } from "Hooks/useProducts";
import { useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  ProductInteractionsWheel,
  ProductKeyStatsCard,
} from "features/analytics";
import {
  ApiChartTemplate,
  ApiChartType,
  ApiDimension,
  ApiMetric,
} from "@incendium/api";
import { AnalyticsSpacing } from "consts";
import GlassCard from "Components/GlassCard/GlassCard";
import AnalyticsCard from "features/analytics/components/AnalyticsCard";
import SelectEmptyState from "features/common/components/SelectEmptyState";
import { ProductDeepDiveHero } from "features/products";
import AnalyticsCardPanelHeader from "features/analytics/components/AnalyticsCardPanelHeader";

const GridContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  display: "grid",
  gridTemplateColumns: "repeat(12, 1fr)",
  gridTemplateRows: "auto",
  gap: theme.spacing(AnalyticsSpacing),
}));

const GridItemLeft = styled(Box)(() => ({
  gridArea: "1 / 1 / 9 / 5",
}));

const GridItemRight = styled(Box)(() => ({
  gridArea: "1 / 5 / 9 / -1",
}));

const GridItemBottomLeft = styled(Box)(() => ({
  minHeight: 400,
  gridArea: "9 / 1 / auto / 7",
}));

const GridItemBottomRight = styled(Box)(() => ({
  minHeight: 400,
  gridArea: "9 / 7 / auto / -1",
}));

interface IProductSinglePageProps {
  dimension: ApiDimension;
  selectedProductId?: number;
  setSelectedProductId?: React.Dispatch<
    React.SetStateAction<number | undefined>
  >;
  selectedProductCategory?: string;
  setSelectedProductCategory?: React.Dispatch<
    React.SetStateAction<string | undefined>
  >;
  selectedProductBrand?: string;
  setSelectedProductBrand?: React.Dispatch<
    React.SetStateAction<string | undefined>
  >;
}

function ProductSinglePage({
  dimension,
  selectedProductId,
  setSelectedProductId,
  selectedProductCategory,
  setSelectedProductCategory,
  selectedProductBrand,
  setSelectedProductBrand,
}: IProductSinglePageProps) {
  const { selectedProject, selectedClient } = useSelectedProject();
  const { products } = useProducts();

  const onClick = useCallback(
    (productName: string) => {
      if (setSelectedProductId) {
        const product = products.find((p) => p.name === productName);
        if (product) {
          setSelectedProductId(product.id);
        }
      }
    },
    [setSelectedProductId, products],
  );

  const selectedProduct = useMemo(
    () => products.find((p) => p.id === selectedProductId),
    [selectedProductId, products],
  );

  const attributes = useMemo(
    () => [
      {
        key: dimension,
        value:
          selectedProduct?.name ||
          selectedProductCategory ||
          selectedProductBrand ||
          "",
      },
    ],
    [dimension, selectedProduct, selectedProductCategory, selectedProductBrand],
  );

  if (!selectedProduct && dimension === ApiDimension.DIMENSION_PRODUCT_NAME) {
    return (
      <SelectEmptyState
        icon={
          <Inventory2Outlined
            sx={{ fontSize: 40, color: "primary.main", opacity: 0.9 }}
          />
        }
        title="Select a product to view insights"
        dropdown={
          <ProductsDropdown
            sx={{ width: "100%", maxWidth: 320 }}
            label="Product"
            variant="outlined"
            value={selectedProductId}
            onChange={(n) => setSelectedProductId && setSelectedProductId(n)}
          />
        }
        table={
          <AnalyticsCard
            onClick={{
              [ApiDimension.DIMENSION_PRODUCT_NAME]: [
                {
                  text: `Select Product`,
                  fn: (p) => onClick(p || ""),
                },
              ],
            }}
            chart={{
              name: `Product Insights`,
              dimension: [ApiDimension.DIMENSION_PRODUCT_NAME],
              template: ApiChartTemplate.TABLE,
              type: ApiChartType.TABLE,
              attributes: [],
              yAxisKeys: [
                {
                  key: "l",
                  fields: [
                    ApiMetric.METRIC_PAGEVIEWS_COUNT,
                    ApiMetric.METRIC_PRODUCT_CLICK_THROUGH_COUNT,
                    ApiMetric.METRIC_PRODUCT_CLICK_THROUGH_RATE,
                    ApiMetric.METRIC_PRODUCT_REVENUE,
                    ApiMetric.METRIC_PRODUCT_IMPRESSIONS_COUNT,
                    ApiMetric.METRIC_PRODUCT_IMPRESSION_RATE,
                    ApiMetric.METRIC_PAGE_ADD_TO_CART_CLICK_COUNT,
                    ApiMetric.METRIC_PAGE_ADD_TO_CART_CLICK_RATE,
                    ApiMetric.METRIC_SALES_COUNT,
                    ApiMetric.METRIC_SALES_COMPLETION_RATE,
                    ApiMetric.METRIC_SALES_REVENUE,
                    ApiMetric.METRIC_VISIT_TO_PURCHASE_RATE,
                  ],
                },
              ],
              displayOptions: {
                noFilters: true,
                noAttributionToggle: true,
                rowsPerPage: 0,
                noTitle: true,
              },
            }}
          />
        }
      />
    );
  }
  if (
    !selectedProductCategory &&
    dimension === ApiDimension.DIMENSION_PRODUCT_CATEGORY
  ) {
    return (
      <SelectEmptyState
        icon={
          <CategoryOutlined
            sx={{ fontSize: 40, color: "primary.main", opacity: 0.9 }}
          />
        }
        title="Select a product category to view insights"
        dropdown={
          <ProductCategoriesDropdown
            sx={{ width: "100%", maxWidth: 320 }}
            label="Product Category"
            variant="outlined"
            value={selectedProductCategory}
            onChange={(n) =>
              setSelectedProductCategory && setSelectedProductCategory(n)
            }
          />
        }
        table={
          <AnalyticsCard
            onClick={{
              [ApiDimension.DIMENSION_PRODUCT_CATEGORY]: [
                {
                  text: `Select Product Category`,
                  fn: (p) =>
                    setSelectedProductCategory &&
                    setSelectedProductCategory(p || ""),
                },
              ],
            }}
            chart={{
              name: `Product Category Insights`,
              dimension: [ApiDimension.DIMENSION_PRODUCT_CATEGORY],
              template: ApiChartTemplate.TABLE,
              type: ApiChartType.TABLE,
              attributes: [],
              yAxisKeys: [
                {
                  key: "l",
                  fields: [
                    ApiMetric.METRIC_PAGEVIEWS_COUNT,
                    ApiMetric.METRIC_PRODUCT_CLICK_THROUGH_COUNT,
                    ApiMetric.METRIC_PRODUCT_CLICK_THROUGH_RATE,
                    ApiMetric.METRIC_PRODUCT_REVENUE,
                    ApiMetric.METRIC_PRODUCT_IMPRESSIONS_COUNT,
                    ApiMetric.METRIC_PRODUCT_IMPRESSION_RATE,
                    ApiMetric.METRIC_PAGE_ADD_TO_CART_CLICK_COUNT,
                    ApiMetric.METRIC_PAGE_ADD_TO_CART_CLICK_RATE,
                    ApiMetric.METRIC_SALES_COUNT,
                    ApiMetric.METRIC_SALES_COMPLETION_RATE,
                    ApiMetric.METRIC_SALES_REVENUE,
                    ApiMetric.METRIC_VISIT_TO_PURCHASE_RATE,
                    ApiMetric.METRIC_PRODUCT_GATEWAY_LTV,
                  ],
                },
              ],
              displayOptions: {
                noFilters: true,
                noAttributionToggle: true,
                rowsPerPage: 0,
                noTitle: true,
              },
            }}
          />
        }
      />
    );
  }
  if (
    !selectedProductBrand &&
    dimension === ApiDimension.DIMENSION_PRODUCT_BRAND
  ) {
    return (
      <SelectEmptyState
        icon={
          <LocalOfferOutlined
            sx={{ fontSize: 40, color: "primary.main", opacity: 0.9 }}
          />
        }
        title="Select a product brand to view insights"
        dropdown={
          <ProductBrandsDropdown
            sx={{ width: "100%", maxWidth: 320 }}
            label="Product Brand"
            variant="outlined"
            value={selectedProductBrand}
            onChange={(n) =>
              setSelectedProductBrand && setSelectedProductBrand(n)
            }
          />
        }
        table={
          <AnalyticsCard
            onClick={{
              [ApiDimension.DIMENSION_PRODUCT_BRAND]: [
                {
                  text: `Select Product Brand`,
                  fn: (p) =>
                    setSelectedProductBrand && setSelectedProductBrand(p || ""),
                },
              ],
            }}
            chart={{
              name: `Product Brand Insights`,
              dimension: [ApiDimension.DIMENSION_PRODUCT_BRAND],
              template: ApiChartTemplate.TABLE,
              type: ApiChartType.TABLE,
              attributes: [],
              yAxisKeys: [
                {
                  key: "l",
                  fields: [
                    ApiMetric.METRIC_PAGEVIEWS_COUNT,
                    ApiMetric.METRIC_PRODUCT_CLICK_THROUGH_COUNT,
                    ApiMetric.METRIC_PRODUCT_CLICK_THROUGH_RATE,
                    ApiMetric.METRIC_PRODUCT_REVENUE,
                    ApiMetric.METRIC_PRODUCT_IMPRESSIONS_COUNT,
                    ApiMetric.METRIC_PRODUCT_IMPRESSION_RATE,
                    ApiMetric.METRIC_PAGE_ADD_TO_CART_CLICK_COUNT,
                    ApiMetric.METRIC_PAGE_ADD_TO_CART_CLICK_RATE,
                    ApiMetric.METRIC_SALES_COUNT,
                    ApiMetric.METRIC_SALES_COMPLETION_RATE,
                    ApiMetric.METRIC_SALES_REVENUE,
                    ApiMetric.METRIC_VISIT_TO_PURCHASE_RATE,
                    ApiMetric.METRIC_PRODUCT_GATEWAY_LTV,
                  ],
                },
              ],
              displayOptions: {
                noFilters: true,
                noAttributionToggle: true,
                rowsPerPage: 0,
                noTitle: true,
              },
            }}
          />
        }
      />
    );
  }

  return (
    <Box pb={4}>
      <Portal container={() => document.getElementById("pageTitle")}>
        <Stack>
          <Typography variant="h1">
            Single Product{" "}
            {dimension === ApiDimension.DIMENSION_PRODUCT_CATEGORY
              ? "Category"
              : dimension === ApiDimension.DIMENSION_PRODUCT_BRAND
                ? "Brand"
                : ""}{" "}
            Dashboard
          </Typography>
          <Stack direction="row" alignItems={"baseline"}>
            <Typography variant="subtitle2" color={"secondary"}>
              {selectedProduct?.name ||
                selectedProductCategory ||
                selectedProductBrand}
            </Typography>
            {dimension === ApiDimension.DIMENSION_PRODUCT_NAME &&
              selectedProduct?.category && (
                <>
                  <Link
                    to={{
                      pathname: `/clients/${selectedClient?.id}/projects/${selectedProject?.id}/analyse/standard-reports/product-category-deep-dive`,
                      state: {
                        selectedProductCategory: selectedProduct?.category,
                      },
                    }}
                  >
                    {" - "}
                    {selectedProduct?.category}
                  </Link>
                </>
              )}
            {dimension === ApiDimension.DIMENSION_PRODUCT_NAME &&
              selectedProduct?.brand && (
                <>
                  <Link
                    to={{
                      pathname: `/clients/${selectedClient?.id}/projects/${selectedProject?.id}/analyse/standard-reports/product-brand-deep-dive`,
                      state: {
                        selectedProductBrand: selectedProduct?.brand,
                      },
                    }}
                  >
                    {" - "}
                    {selectedProduct?.brand}
                  </Link>
                </>
              )}
          </Stack>
        </Stack>
      </Portal>

      <GridContainer>
        {/* First Box remains the same on both screen sizes */}
        <GridItemLeft>
          <Stack spacing={AnalyticsSpacing} sx={{ height: "100%" }}>
            <ProductDeepDiveHero
              dimension={dimension}
              selectedProduct={selectedProduct}
              selectedProductCategory={selectedProductCategory}
              selectedProductBrand={selectedProductBrand}
            />
            <AnalyticsChartStatCard
              hideTrendedSpark
              noToolbar
              forceComparison
              icon={<AttachMoney />}
              chart={{
                name: "Total Revenue",
                dimension: [],
                template: ApiChartTemplate.SNAPSHOT,
                type: ApiChartType.GRAPH,
                attributes,
                comparison: true,
                yAxisKeys: [
                  {
                    key: "l",
                    fields: [ApiMetric.METRIC_PRODUCT_REVENUE],
                  },
                ],
                displayOptions: {
                  statTitle: "Total Revenue",
                },
              }}
            />
            <ProductKeyStatsCard
              selectedDimension={
                selectedProduct?.name ||
                selectedProductCategory ||
                selectedProductBrand ||
                ""
              }
              productDimension={dimension}
            />
          </Stack>
        </GridItemLeft>

        {/* For smaller screens, take us rest of space */}
        <GridItemRight>
          <GlassCard
            height="100%"
            sx={{ height: "100%" }}
            boxProps={{
              p: 2.5,
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <AnalyticsCardPanelHeader
              icon={<HubOutlined fontSize="small" />}
              title="Product Interactions"
            />
            <Box sx={{ flex: 1, minHeight: 0, mt: 1 }}>
              <ProductInteractionsWheel attributes={attributes} />
            </Box>
          </GlassCard>
        </GridItemRight>

        {/* For smaller screens, these two boxes will be side-by-side on a new row */}
        <GridItemBottomLeft>
          <ImpressionsByDayChart
            dimension={dimension}
            impressionField={ApiMetric.METRIC_PRODUCT_IMPRESSIONS_COUNT}
            type="product"
            selected={
              selectedProduct?.name ||
              selectedProductCategory ||
              selectedProductBrand ||
              ""
            }
          />
        </GridItemBottomLeft>
        <GridItemBottomRight>
          <PrimaryTopMicroConversions
            dimension={dimension}
            byDimension={
              selectedProduct?.name ||
              selectedProductCategory ||
              selectedProductBrand ||
              ""
            }
            dimensionString={`product ${
              dimension === ApiDimension.DIMENSION_PRODUCT_CATEGORY
                ? "category"
                : dimension === ApiDimension.DIMENSION_PRODUCT_BRAND
                  ? "brand"
                  : ""
            }`}
          />
        </GridItemBottomRight>
      </GridContainer>

      <Grid container spacing={AnalyticsSpacing} mt={0}>
        <Grid item xs={7} sx={{ height: table5Rows }}>
          <AnalyticsCard
            chart={{
              name: `Product Landing Page Performance by`,
              dimension: [ApiDimension.DIMENSION_CHANNEL],
              template: ApiChartTemplate.TABLE,
              type: ApiChartType.TABLE,
              attributes: [
                {
                  key: dimension,
                  value:
                    selectedProduct?.name ||
                    selectedProductCategory ||
                    selectedProductBrand,
                },
                {
                  key: ApiDimension.DIMENSION_IS_LANDING_PAGE,
                  value: "true",
                },
              ],
              comparison: true,
              yAxisKeys: [
                {
                  key: "l",
                  fields: [
                    ApiMetric.METRIC_SESSIONS_COUNT,
                    ApiMetric.METRIC_EFFECTIVE_SESSION_RATE,
                    ApiMetric.METRIC_PAGE_MICRO_CONVERSION_RATE,
                    ApiMetric.METRIC_AVERAGE_TIME_ON_PAGE,
                    ApiMetric.METRIC_AVERAGE_VIEWED_PERCENTAGE,
                    ApiMetric.METRIC_BOUNCE_RATE,
                    ApiMetric.METRIC_SESSION_ADD_TO_CART_CLICK_RATE,
                    ApiMetric.METRIC_CHECKOUT_COMPLETION_RATE,
                    ApiMetric.METRIC_SALES_COMPLETION_RATE,
                    ApiMetric.METRIC_PRODUCT_REVENUE,
                  ],
                },
              ],
              displayOptions: {
                noFilters: true,
                availableDimensions: [
                  ApiDimension.DIMENSION_CHANNEL,
                  ApiDimension.DIMENSION_SOURCE,
                  ApiDimension.DIMENSION_MEDIUM,
                  ApiDimension.DIMENSION_CAMPAIGN,
                  ApiDimension.DIMENSION_TERM,
                ],
                // noTitle: true,
              },
            }}
          />
        </Grid>
        <Grid item xs={5}>
          <AnalyticsCard
            chart={{
              name: `Product Page Performance`,
              dimension: [],
              template: ApiChartTemplate.TABLE,
              type: ApiChartType.TABLE,
              attributes: [
                {
                  key: dimension,
                  value:
                    selectedProduct?.name ||
                    selectedProductCategory ||
                    selectedProductBrand,
                },
              ],
              comparison: true,
              yAxisKeys: [
                {
                  key: "l",
                  fields: [
                    ApiMetric.METRIC_PAGEVIEWS_COUNT,
                    ApiMetric.METRIC_EFFECTIVE_PAGEVIEW_RATE,
                    ApiMetric.METRIC_PAGE_MICRO_CONVERSION_RATE,
                    ApiMetric.METRIC_AVERAGE_TIME_ON_PAGE,
                    ApiMetric.METRIC_AVERAGE_VIEWED_PERCENTAGE,
                    ApiMetric.METRIC_EXIT_RATE,
                    ApiMetric.METRIC_PAGE_ADD_TO_CART_CLICK_RATE,
                    ApiMetric.METRIC_CHECKOUT_COMPLETION_RATE,
                    ApiMetric.METRIC_VISIT_TO_PURCHASE_RATE,
                    ApiMetric.METRIC_AVERAGE_CWV_PERFORMANCE_SCORE,
                  ],
                },
              ],
              displayOptions: {
                noFilters: true,

                // noTitle: true,
              },
            }}
          />
        </Grid>
      </Grid>
    </Box>
  );
}

export default ProductSinglePage;
